import React, { useState } from "react";
import { GraduationCap, Briefcase, FolderGit2, Brain, Award, Plus, Edit2, Trash2, MapPin, Calendar, ExternalLink, Clock } from "lucide-react";
import ProfileHeader from "../components/profile/ProfileHeader";
import Modal from "../components/profile/Modal";
import { useAuthStore } from "../store/authStore";
import axios from "axios";
import Navigation from "../components/landing/Navigation";
import Footer from "../components/landing/Footer";

const ProfilePage = () => {
    const { user, setUser } = useAuthStore();

    const apiUrl = import.meta.env.VITE_API_PORT;

    // ===== Profile Data from user =====
    const profile = {
        name: user?.name || "",
        email: user?.email || "",
        phone: user?.phone || "",
        location: user?.location || "",
        company: user?.company || "",
        job_title: user?.job_title || "",
        avatar: user?.avatar || "",
        badges: user?.badges || [],
        tier: user?.tier || "",
        points: user?.points || 0,
        createdAt: user?.createdAt || "",
    };

    // ===== State for Sections =====
    const [education, setEducation] = useState(user?.education || []);
    const [workExperience, setWorkExperience] = useState(user?.work || []);
    const [projects, setProjects] = useState(user?.projects || []);
    const [skills, setSkills] = useState(user?.skills || []);
    const [certificates, setCertificates] = useState(user?.certificates || []);

    // ===== Modal State =====
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);

    const openModal = (type, item = null) => {
        setModalType(type);
        setEditingItem(item);
        setFormData(item || {});
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingItem(null);
        setFormData({});
    };

    // ===== Delete Handlers =====
    const handleDelete = async (type, id) => {
        let updatedData;
        let fieldName;

        switch (type) {
            case "education":
                updatedData = education.filter((e) => e.id !== id);
                setEducation(updatedData);
                fieldName = "education";
                break;
            case "work":
                updatedData = workExperience.filter((w) => w.id !== id);
                setWorkExperience(updatedData);
                fieldName = "work";
                break;
            case "project":
                updatedData = projects.filter((p) => p.id !== id);
                setProjects(updatedData);
                fieldName = "projects";
                break;
            case "skill":
                updatedData = skills.filter((s) => s.id !== id);
                setSkills(updatedData);
                fieldName = "skills";
                break;
            case "certificate":
                updatedData = certificates.filter((c) => c.id !== id);
                setCertificates(updatedData);
                fieldName = "certificates";
                break;
            default:
                return;
        }

        try {
            const res = await axios.put(
                `${apiUrl}/api/users/${user._id}`,
                { [fieldName]: updatedData }
            );
            setUser(res.data);
        } catch (err) {
            console.error(`Error deleting ${type}:`, err);
        }
    };

    // ===== Save Handler with API Integration =====
    const handleSave = async () => {
        let updatedData;

        switch (modalType) {
            case "education":
                updatedData = editingItem
                    ? education.map((e) => (e.id === editingItem.id ? formData : e))
                    : [...education, { ...formData, id: Date.now() }];
                setEducation(updatedData);

                try {
                    setLoading(true);
                    const res = await axios.put(
                        `${apiUrl}/api/users/${user._id}`,
                        { education: updatedData }
                    );
                    setUser(res.data);
                } catch (err) {
                    console.error("Error updating education:", err);
                } finally {
                    setLoading(false);
                }
                break;

            case "work":
                updatedData = editingItem
                    ? workExperience.map((w) => (w.id === editingItem.id ? formData : w))
                    : [...workExperience, { ...formData, id: Date.now() }];
                setWorkExperience(updatedData);

                try {
                    setLoading(true);
                    const res = await axios.put(
                        `${apiUrl}/api/users/${user._id}`,
                        { work: updatedData }
                    );
                    setUser(res.data);
                } catch (err) {
                    console.error("Error updating work experience:", err);
                } finally {
                    setLoading(false);
                }
                break;

            case "project":
                updatedData = editingItem
                    ? projects.map((p) => (p.id === editingItem.id ? formData : p))
                    : [...projects, { ...formData, id: Date.now() }];
                setProjects(updatedData);

                try {
                    setLoading(true);
                    const res = await axios.put(
                        `${apiUrl}/api/users/${user._id}`,
                        { projects: updatedData }
                    );
                    setUser(res.data);
                } catch (err) {
                    console.error("Error updating projects:", err);
                } finally {
                    setLoading(false);
                }
                break;

            case "skill":
                updatedData = editingItem
                    ? skills.map((s) => (s.id === editingItem.id ? formData : s))
                    : [...skills, { ...formData, id: Date.now() }];
                setSkills(updatedData);

                try {
                    setLoading(true);
                    const res = await axios.put(
                        `${apiUrl}/api/users/${user._id}`,
                        { skills: updatedData }
                    );
                    setUser(res.data);
                } catch (err) {
                    console.error("Error updating skills:", err);
                } finally {
                    setLoading(false);
                }
                break;

            case "certificate":
                updatedData = editingItem
                    ? certificates.map((c) => (c.id === editingItem.id ? formData : c))
                    : [...certificates, { ...formData, id: Date.now() }];
                setCertificates(updatedData);

                try {
                    setLoading(true);
                    const res = await axios.put(
                        `${apiUrl}/api/users/${user._id}`,
                        { certificates: updatedData }
                    );
                    setUser(res.data);
                } catch (err) {
                    console.error("Error updating certificates:", err);
                } finally {
                    setLoading(false);
                }
                break;

            default:
                break;
        }

        closeModal();
    };

    // ===== Render Card Content =====
    const renderCardContent = (type, item) => {
        switch (type) {
            case "education":
                return (
                    <div className="space-y-3">
                        <div>
                            <h4 className="text-lg font-semibold text-white">
                                {item.degree || item.level}
                            </h4>
                            <p className="text-gray-400">{item.institution}</p>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {item.startYear} - {item.endYear}
                            </span>
                            {item.performance && (
                                <span className="px-2 py-1 bg-green-500/10 text-green-400 rounded-full">
                                    {item.performance} {item.scoreType}
                                </span>
                            )}
                        </div>
                        {item.stream && <p className="text-gray-400 text-sm">{item.stream}</p>}
                        {item.board && <p className="text-gray-400 text-sm">Board: {item.board}</p>}
                    </div>
                );

            case "work":
                return (
                    <div className="space-y-3">
                        <div>
                            <h4 className="text-lg font-semibold text-white">{item.designation}</h4>
                            <p className="text-gray-400">{item.organization}</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                            {item.location && (
                                <span className="flex items-center gap-1">
                                    <MapPin className="w-4 h-4" />
                                    {item.location}
                                </span>
                            )}
                            <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {item.startDate} - {item.currentlyWorking ? "Present" : item.endDate}
                            </span>
                            {item.currentlyWorking && (
                                <span className="px-2 py-1 bg-green-500/10 text-green-400 rounded-full text-xs">
                                    Current
                                </span>
                            )}
                        </div>
                        {item.description && (
                            <p className="text-gray-400 text-sm line-clamp-2">{item.description}</p>
                        )}
                    </div>
                );

            case "project":
                return (
                    <div className="space-y-3">
                        <div className="flex items-start justify-between">
                            <h4 className="text-lg font-semibold text-white">{item.title}</h4>
                            {item.projectLink && (
                                <a
                                    href={item.projectLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-400 hover:text-green-300 transition-colors"
                                >
                                    <ExternalLink className="w-5 h-5" />
                                </a>
                            )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {item.startMonth} - {item.currentlyOngoing ? "Ongoing" : item.endMonth}
                            </span>
                            {item.currentlyOngoing && (
                                <span className="px-2 py-1 bg-green-500/10 text-green-400 rounded-full text-xs">
                                    Active
                                </span>
                            )}
                        </div>
                        {item.description && (
                            <p className="text-gray-400 text-sm line-clamp-2">{item.description}</p>
                        )}
                    </div>
                );

            case "certificate":
                return (
                    <div className="space-y-3">
                        <div className="flex items-start justify-between">
                            <div>
                                <h4 className="text-lg font-semibold text-white">{item.name}</h4>
                                <p className="text-gray-400">{item.issuer}</p>
                            </div>
                            {item.credentialUrl && (
                                <a
                                    href={item.credentialUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-400 hover:text-green-300 transition-colors"
                                >
                                    <ExternalLink className="w-5 h-5" />
                                </a>
                            )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Calendar className="w-4 h-4" />
                            {item.date}
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    // ===== Section Configurations =====
    const sections = [
        {
            id: "education",
            title: "Education",
            icon: GraduationCap,
            color: "from-blue-500 to-cyan-500",
            data: education,
            emptyText: "Add your educational background"
        },
        {
            id: "work",
            title: "Work Experience",
            icon: Briefcase,
            color: "from-purple-500 to-pink-500",
            data: workExperience,
            emptyText: "Share your professional experience"
        },
        {
            id: "project",
            title: "Projects",
            icon: FolderGit2,
            color: "from-orange-500 to-red-500",
            data: projects,
            emptyText: "Showcase your projects"
        },
        {
            id: "skill",
            title: "Skills",
            icon: Brain,
            color: "from-green-500 to-emerald-500",
            data: skills,
            emptyText: "List your technical skills"
        },
        {
            id: "certificate",
            title: "Certificates",
            icon: Award,
            color: "from-yellow-500 to-amber-500",
            data: certificates,
            emptyText: "Add your certifications"
        }
    ];

    // ===== Render Form =====
    const renderForm = () => {
        switch (modalType) {
            case "education":
                return (
                    <div className="space-y-4">
                        <select
                            value={formData.level || ""}
                            onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                            className="w-full p-2 rounded bg-gray-800 text-white"
                        >
                            <option value="">Select Education Level</option>
                            <option value="secondary">Secondary (X)</option>
                            <option value="seniorSecondary">Senior Secondary / Equivalent (XII)</option>
                            <option value="graduation">Graduation / Post Graduation</option>
                            <option value="diploma">Diploma</option>
                            <option value="phd">PhD</option>
                        </select>

                        <input
                            type="text"
                            placeholder="Institution / School / College"
                            value={formData.institution || ""}
                            onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                            className="w-full p-2 rounded bg-gray-800 text-white"
                        />

                        <div className="flex gap-3">
                            <input
                                type="text"
                                placeholder="Start Year"
                                value={formData.startYear || ""}
                                onChange={(e) => setFormData({ ...formData, startYear: e.target.value })}
                                className="w-1/2 p-2 rounded bg-gray-800 text-white"
                            />
                            <input
                                type="text"
                                placeholder="End Year"
                                value={formData.endYear || ""}
                                onChange={(e) => setFormData({ ...formData, endYear: e.target.value })}
                                className="w-1/2 p-2 rounded bg-gray-800 text-white"
                            />
                        </div>

                        {formData.level === "secondary" && (
                            <div className="space-y-3">
                                <input
                                    type="text"
                                    placeholder="Board (e.g. CBSE, ICSE, State Board)"
                                    value={formData.board || ""}
                                    onChange={(e) => setFormData({ ...formData, board: e.target.value })}
                                    className="w-full p-2 rounded bg-gray-800 text-white"
                                />
                            </div>
                        )}

                        {formData.level === "seniorSecondary" && (
                            <div className="space-y-3">
                                <input
                                    type="text"
                                    placeholder="Board (e.g. CBSE, ICSE, State Board)"
                                    value={formData.board || ""}
                                    onChange={(e) => setFormData({ ...formData, board: e.target.value })}
                                    className="w-full p-2 rounded bg-gray-800 text-white"
                                />
                                <input
                                    type="text"
                                    placeholder="Stream (e.g. Science, Commerce, Arts)"
                                    value={formData.stream || ""}
                                    onChange={(e) => setFormData({ ...formData, stream: e.target.value })}
                                    className="w-full p-2 rounded bg-gray-800 text-white"
                                />
                            </div>
                        )}

                        {(formData.level === "graduation" ||
                            formData.level === "diploma" ||
                            formData.level === "phd") && (
                                <div className="space-y-3">
                                    <input
                                        type="text"
                                        placeholder="Degree (e.g. B.Sc (Hons.), MBA, Diploma in IT, PhD in Physics)"
                                        value={formData.degree || ""}
                                        onChange={(e) => setFormData({ ...formData, degree: e.target.value })}
                                        className="w-full p-2 rounded bg-gray-800 text-white"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Specialization / Stream"
                                        value={formData.stream || ""}
                                        onChange={(e) => setFormData({ ...formData, stream: e.target.value })}
                                        className="w-full p-2 rounded bg-gray-800 text-white"
                                    />
                                </div>
                            )}

                        <div className="flex gap-3">
                            <select
                                value={formData.scoreType || "Percentage"}
                                onChange={(e) => setFormData({ ...formData, scoreType: e.target.value })}
                                className="w-1/3 p-2 rounded bg-gray-800 text-white"
                            >
                                <option value="Percentage">Percentage</option>
                                <option value="CGPA">CGPA</option>
                            </select>
                            <input
                                type="text"
                                placeholder="Score (e.g. 85 / 9.2)"
                                value={formData.performance || ""}
                                onChange={(e) => setFormData({ ...formData, performance: e.target.value })}
                                className="w-2/3 p-2 rounded bg-gray-800 text-white"
                            />
                        </div>
                    </div>
                );

            case "work":
                return (
                    <div className="space-y-3">
                        <select
                            value={formData.workType || ""}
                            onChange={(e) => setFormData({ ...formData, workType: e.target.value })}
                            className="w-full p-2 rounded bg-gray-800 text-white"
                        >
                            <option value="">Select Work Type</option>
                            <option value="fullTime">Full-time</option>
                            <option value="partTime">Part-time</option>
                            <option value="internship">Internship</option>
                            <option value="volunteering">Volunteering</option>
                        </select>

                        <input
                            type="text"
                            placeholder="Designation (e.g. Software Engineer)"
                            value={formData.designation || ""}
                            onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                            className="w-full p-2 rounded bg-gray-800 text-white"
                        />

                        {formData.workType === "fullTime" && (
                            <input
                                type="text"
                                placeholder="Profile (e.g. Operations)"
                                value={formData.profile || ""}
                                onChange={(e) => setFormData({ ...formData, profile: e.target.value })}
                                className="w-full p-2 rounded bg-gray-800 text-white"
                            />
                        )}

                        <input
                            type="text"
                            placeholder="Organization (e.g. Internshala)"
                            value={formData.organization || ""}
                            onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                            className="w-full p-2 rounded bg-gray-800 text-white"
                        />

                        <input
                            type="text"
                            placeholder="Location (e.g. Mumbai)"
                            value={formData.isRemote ? "Remote" : formData.location || ""}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    location: e.target.value,
                                    isRemote: false,
                                })
                            }
                            className="w-full p-2 rounded bg-gray-800 text-white"
                            disabled={formData.isRemote}
                        />

                        <label className="flex items-center gap-2 text-gray-300">
                            <input
                                type="checkbox"
                                checked={formData.isRemote || false}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        isRemote: e.target.checked,
                                        location: e.target.checked ? "Remote" : "",
                                    })
                                }
                            />
                            Is work from home
                        </label>

                        <div className="flex gap-3">
                            <input
                                type="date"
                                value={formData.startDate || ""}
                                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                className="w-1/2 p-2 rounded bg-gray-800 text-white"
                            />
                            <input
                                type="date"
                                value={formData.endDate || ""}
                                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                disabled={formData.currentlyWorking}
                                className="w-1/2 p-2 rounded bg-gray-800 text-white"
                            />
                        </div>

                        <label className="flex items-center gap-2 text-gray-300">
                            <input
                                type="checkbox"
                                checked={formData.currentlyWorking || false}
                                onChange={(e) => setFormData({ ...formData, currentlyWorking: e.target.checked })}
                            />
                            Currently working here
                        </label>

                        <textarea
                            placeholder="Description (e.g. responsibilities, achievements, skills used)"
                            value={formData.description || ""}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full p-2 rounded bg-gray-800 text-white"
                            rows={4}
                        />
                    </div>
                );

            case "project":
                return (
                    <div className="space-y-3">
                        <input
                            type="text"
                            placeholder="Project Title"
                            value={formData.title || ""}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="w-full p-2 rounded bg-gray-800 text-white"
                        />

                        <div className="flex gap-3">
                            <input
                                type="month"
                                value={formData.startMonth || ""}
                                onChange={(e) => setFormData({ ...formData, startMonth: e.target.value })}
                                className="w-1/2 p-2 rounded bg-gray-800 text-white"
                            />
                            <input
                                type="month"
                                value={formData.endMonth || ""}
                                onChange={(e) => setFormData({ ...formData, endMonth: e.target.value })}
                                disabled={formData.currentlyOngoing}
                                className="w-1/2 p-2 rounded bg-gray-800 text-white"
                            />
                        </div>

                        <label className="flex items-center gap-2 text-gray-300">
                            <input
                                type="checkbox"
                                checked={formData.currentlyOngoing || false}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        currentlyOngoing: e.target.checked,
                                        endMonth: e.target.checked ? "" : formData.endMonth,
                                    })
                                }
                            />
                            Currently ongoing
                        </label>

                        <textarea
                            placeholder="Description (optional)"
                            value={formData.description || ""}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full p-2 rounded bg-gray-800 text-white"
                            rows={3}
                        />

                        <input
                            type="url"
                            placeholder="Project Link (optional)"
                            value={formData.projectLink || ""}
                            onChange={(e) => setFormData({ ...formData, projectLink: e.target.value })}
                            className="w-full p-2 rounded bg-gray-800 text-white"
                        />
                    </div>
                );

            case "skill":
                return (
                    <div className="space-y-3">
                        <input
                            type="text"
                            placeholder="Skill"
                            value={formData.name || ""}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full p-2 rounded bg-gray-800 text-white"
                        />
                    </div>
                );

            case "certificate":
                return (
                    <div className="space-y-3">
                        <input
                            type="text"
                            placeholder="Certificate Name"
                            value={formData.name || ""}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full p-2 rounded bg-gray-800 text-white"
                        />

                        <input
                            type="text"
                            placeholder="Issuer"
                            value={formData.issuer || ""}
                            onChange={(e) => setFormData({ ...formData, issuer: e.target.value })}
                            className="w-full p-2 rounded bg-gray-800 text-white"
                        />

                        <input
                            type="month"
                            placeholder="Month & Year"
                            value={formData.date || ""}
                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                            className="w-full p-2 rounded bg-gray-800 text-white"
                        />

                        <input
                            type="url"
                            placeholder="Credential URL (optional)"
                            value={formData.credentialUrl || ""}
                            onChange={(e) => setFormData({ ...formData, credentialUrl: e.target.value })}
                            className="w-full p-2 rounded bg-gray-800 text-white"
                        />
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <>
            <Navigation />
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
                <ProfileHeader profile={profile} />

                <div className="max-w-6xl mx-auto px-6 py-12 space-y-12">
                    {sections.map((section) => {
                        const Icon = section.icon;
                        return (
                            <div key={section.id} className="group">
                                {/* Section Header */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-3">
                                        <div className={`p-3 rounded-xl bg-gradient-to-br ${section.color} shadow-lg`}>
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-2xl font-bold text-white">{section.title}</h2>
                                            <p className="text-gray-500 text-sm">
                                                {section.data.length} {section.data.length === 1 ? "item" : "items"}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Floating Add Button */}
                                    <button
                                        onClick={() => openModal(section.id)}
                                        className={`group/btn relative px-5 py-3 bg-gradient-to-r ${section.color} rounded-full 
                                            font-semibold text-white shadow-lg hover:shadow-xl transform hover:scale-105 
                                            transition-all duration-300 flex items-center gap-2`}
                                    >
                                        <Plus className="w-5 h-5 group-hover/btn:rotate-90 transition-transform duration-300" />
                                        <span>Add New</span>
                                    </button>
                                </div>

                                {/* Content Grid */}
                                {section.data.length === 0 ? (
                                    // Empty State
                                    <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-12 text-center">
                                        <Icon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                                        <p className="text-gray-400 text-lg">{section.emptyText}</p>
                                    </div>
                                ) : section.id === "skill" ? (
                                    // Skills Special Layout
                                    <div className="flex flex-wrap gap-3">
                                        {section.data.map((skill) => (
                                            <div
                                                key={skill.id}
                                                className="group/card relative px-5 py-3 bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 
                                                    rounded-xl hover:bg-gray-700/50 hover:border-green-500/50 transition-all duration-300
                                                    hover:shadow-lg hover:shadow-green-500/10 hover:-translate-y-1"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <span className="text-white font-medium">{skill.name}</span>
                                                    <div className="opacity-0 group-hover/card:opacity-100 transition-opacity flex gap-1">
                                                        <button
                                                            onClick={() => openModal("skill", skill)}
                                                            className="p-1 hover:bg-gray-600 rounded transition-colors"
                                                        >
                                                            <Edit2 className="w-3 h-3 text-gray-400" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete("skill", skill.id)}
                                                            className="p-1 hover:bg-red-500/20 rounded transition-colors"
                                                        >
                                                            <Trash2 className="w-3 h-3 text-red-400" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    // Standard Card Grid
                                    <div className="grid md:grid-cols-2 gap-6">
                                        {section.data.map((item) => (
                                            <div
                                                key={item.id}
                                                className="group/card relative bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 
                                                    rounded-2xl p-6 hover:bg-gray-800/70 hover:border-gray-600 transition-all duration-300
                                                    hover:shadow-xl hover:shadow-gray-900/50 hover:-translate-y-1"
                                            >
                                                {/* Card Actions */}
                                                <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover/card:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={() => openModal(section.id, item)}
                                                        className="p-2 bg-gray-700/80 hover:bg-green-600 rounded-lg transition-colors"
                                                    >
                                                        <Edit2 className="w-4 h-4 text-white" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(section.id, item.id)}
                                                        className="p-2 bg-gray-700/80 hover:bg-red-600 rounded-lg transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4 text-white" />
                                                    </button>
                                                </div>

                                                {/* Card Content */}
                                                {renderCardContent(section.id, item)}

                                                {/* Decorative Gradient Border on Hover */}
                                                <div
                                                    className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${section.color} opacity-0 
                                                        group-hover/card:opacity-10 transition-opacity pointer-events-none`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <Modal
                    show={showModal}
                    title={loading ? "Saving..." : editingItem ? "Edit Item" : "Add New Item"}
                    onSave={handleSave}
                    onClose={closeModal}
                >
                    {renderForm()}
                </Modal>
            </div>

            <Footer />
        </>
    );
};

export default ProfilePage;